import React, { memo, useEffect, useState } from 'react'
import { Button } from 'antd'
import { useLocation } from 'react-router-dom'
import { FiltersFastButtonsRow, FiltersFastButtonsWrapper } from './styles'
import { useMediaQuery } from 'react-responsive'

interface IFilter {
  name: string
  code: string
  value: string
  style?: React.CSSProperties
}

interface IButton extends Omit<IFilter, 'style'> {
  isActive: boolean
}

interface IFilterButtons {
  filters: IFilter[]
  onChange: (params: Record<string, string | string[]>, replace?: boolean) => void
}

export const FiltersFastButtons = memo(({ filters, onChange }: IFilterButtons) => {
  const location = useLocation()
  const [buttons, setButtons] = useState<IButton[]>([])
  const [isUpd, setIsUpd] = useState(false)
  const isMobile = useMediaQuery({ maxWidth: 768 })

  useEffect(() => {
    if (!filters) {
      return
    }

    const urlSearchParams = new URLSearchParams(location.search)
    const buttons = filters.map(filter => {
      const currentFilters = urlSearchParams.get(filter.code)?.split(',') || []
      const buttonValues = filter.value.split(',')

      const isActive =
        buttonValues.length > 0 && buttonValues.every(val => currentFilters.includes(val))

      return {
        name: filter.name,
        code: filter.code,
        value: filter.value,
        isActive,
      }
    })

    setButtons(buttons)
  }, [filters, location])

  useEffect(() => {
    if (!isUpd) return

    const urlSearchParams = new URLSearchParams(location.search)
    const newParams: Record<string, string> = {}
    const groupedFilters: Record<string, string[]> = {}

    urlSearchParams.forEach((value, key) => {
      if (!filters.some(filter => filter.code === key)) {
        newParams[key] = value
      }
    })

    buttons.forEach(button => {
      if (button.isActive && button.value !== 'all') {
        if (!groupedFilters[button.code]) {
          groupedFilters[button.code] = []
        }
        const buttonValues = button.value.split(',')
        groupedFilters[button.code].push(...buttonValues)
      }
    })

    Object.entries(groupedFilters).forEach(([code, values]) => {
      if (values.length > 0) {
        const uniqueValues = [...new Set(values)]
        newParams[code] = uniqueValues.join(',')
      }
    })

    onChange(newParams, true)
    setIsUpd(false)
  }, [buttons, location, onChange, filters])

  const onClickButton = (value: string, code: string) => () => {
    setButtons(prevState => {
      const newState = prevState.map(btn => {
        if (btn.value === value && btn.code === code) {
          return { ...btn, isActive: !btn.isActive }
        }
        return btn
      })
      return newState
    })
    setIsUpd(true)
  }

  return (
    <FiltersFastButtonsWrapper>
      <FiltersFastButtonsRow>
        {buttons.map(button => {
          const filter = filters.find(f => f.code === button.code && f.value === button.value)
          const buttonStyle = filter?.style || {}

          return (
            <Button
              key={`${button.code}-${button.value}`}
              type={button.isActive ? 'primary' : 'default'}
              onClick={onClickButton(button.value, button.code)}
              size={isMobile ? 'middle' : 'small'}
              style={{
                color: button.isActive ? buttonStyle.color : 'inherit',
                backgroundColor: button.isActive ? buttonStyle.backgroundColor : '#00000005',
                border: `1px solid ${buttonStyle.backgroundColor || '#d9d9d9'}`,
                lineHeight: 'normal',
                boxShadow: 'none',
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: button.name }} />
            </Button>
          )
        })}
      </FiltersFastButtonsRow>
    </FiltersFastButtonsWrapper>
  )
})

FiltersFastButtons.displayName = 'FiltersFastButtons'
